import dayjs from 'dayjs';

// ---- number formatting: Indian grouping, no decimals, ₹ lives in the header not in every cell ----
export const inr = (v) => Math.round(Number(v) || 0).toLocaleString('en-IN');
// zeros print as a quiet dash so the numbers that matter stand out (a sheet user scans for values)
export const dashZero = (v) => (Number(v) ? inr(v) : '–');
export const pct = (v, decimals = 1) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(decimals)}%`);

// ---- small screens: an amount that does not fit its column is shown in lakh / crore ----
// From 1 lakh up, 5,24,318 reads 5.24L and 1,25,00,000 reads 1.25Cr; smaller amounts stay in full (6 characters at most).
// Which form a table uses is decided by `amountFits`: the full amount when it fits its column, the short one when it would not - so a
// number is never cut or drawn over its neighbour. Nothing is hidden: the full figure is in the tooltip / the dialogs.
const trimmed = (n, decimals) => String(Number(n.toFixed(decimals)));
// `thousands`: for the very narrowest columns amounts from 1,000 up are written 12.5K as well
export const compactInr = (v, thousands = false) => {
  const n = Math.round(Number(v) || 0);
  const a = Math.abs(n);
  if (a >= 1e7) return `${trimmed(n / 1e7, a >= 1e9 ? 0 : a >= 1e8 ? 1 : 2)}Cr`;
  if (a >= 1e5) return `${trimmed(n / 1e5, a >= 1e6 ? 1 : 2)}L`;
  if (thousands && a >= 1e3) return `${trimmed(n / 1e3, a >= 1e4 ? 1 : 2)}K`;
  return inr(n);
};
export const compactDash = (v, thousands = false) => (Number(v) ? compactInr(v, thousands) : '–');
// the short names of the projects, for a column too narrow for the full one
// (two steps: a short name, and for the very narrowest columns an initial-style one - the project's colour and the full name on hover still say which)
export const PROJECT_ABBREVIATIONS = { foundation: 'Found.', classroom: 'Class', littleGenie: 'L. Genie', stc: 'STC', speakPro: 'Speak Pro', skillx: 'SkillX' };
export const PROJECT_INITIALS = { foundation: 'Fndn', classroom: 'Class', littleGenie: 'LG', stc: 'STC', speakPro: 'SP', skillx: 'SkX' };
// roughly how wide `chars` digits are at `fontPx` (tabular figures: about 0.58 of the font size each, 0.63 in bold) plus the cell padding
export const amountFits = (chars, colPx, fontPx, bold = false) => chars * fontPx * (bold ? 0.63 : 0.58) + 10 <= colPx;
// the type size a column of this width can carry
export const fontForColumn = (colPx) => (colPx >= 96 ? 13 : colPx >= 78 ? 12 : colPx >= 62 ? 11.5 : colPx >= 50 ? 10.5 : 10);

// ---- TAR colour bands. One place to change if the business wants different cut-offs. ----
export const TAR_BANDS = [
  { min: 100, label: '100%+ on target', fg: '#065F46', bg: '#BBF7D0' },
  { min: 75, label: '75–99%', fg: '#92400E', bg: '#FDE68A' },
  { min: 0, label: 'Below 75%', fg: '#991B1B', bg: '#FECACA' },
];
export const tarBand = (tar) => (tar === null || tar === undefined ? null : TAR_BANDS.find((b) => tar >= b.min) || TAR_BANDS[2]);

// ---- colour system: ONE look for the whole page, the Accounts Manager's (a single blue, plain tables with fine grid lines). Colour is kept
// only where it tells things apart: a project/department keeps the SAME colour here as in the Accounts Manager (Classroom green, Foundation
// amber, Little Genie pink, Speak Pro teal), Sales revenue is green and Fee revenue amber, the TAR bands are green/amber/red.
export const THEME = {
  primary: '#1565C0', // the Accounts Manager's table-header blue
  primaryDark: '#0D47A1',
  primarySoft: '#E3F2FD',
  primaryLine: '#BBDEFB',
  rowAlt: '#F8FAFC',
  rowHover: '#EEF5FD',
  grid: '#CBD5E1', // the Accounts Manager's cell grid line
  total: '#F1F5F9', // a Total row: the Accounts Manager's month-total row
};
// the title-bar / controls card, the same on both sides of the page
export const PANEL_SX = { backgroundColor: 'white', borderRadius: '12px', borderTop: `4px solid ${THEME.primary}`, boxShadow: '0 1px 6px rgba(15,23,42,0.10)' };
export const PROJECT_COLORS = {
  foundation: { main: '#F59E0B', soft: '#FEF3C7', text: '#92400E' },
  classroom: { main: '#10B981', soft: '#D1FAE5', text: '#065F46' },
  littleGenie: { main: '#EC4899', soft: '#FCE7F3', text: '#9D174D' },
  stc: { main: '#8B5CF6', soft: '#EDE9FE', text: '#5B21B6' },
  speakPro: { main: '#0D9488', soft: '#CCFBF1', text: '#115E59' },
  skillx: { main: '#F97316', soft: '#FFEDD5', text: '#9A3412' },
};
const FALLBACK_PROJECT_COLOR = { main: '#1565C0', soft: '#E3F2FD', text: '#0D47A1' };
export const projectColor = (key) => PROJECT_COLORS[key] || FALLBACK_PROJECT_COLOR;

// the department names a very narrow column can carry (daily table, week table)
export const DEPT_ABBREVIATIONS = { 3: 'Found.', 20: 'Class', 21: 'L. Genie', 22: 'Speak Pro' };

// ---- Expand / Collapse: the page OPENS EXPANDED (both tables show every row and the page scrolls); a choice made earlier in this browser is kept
export const EXPANDED_STORAGE_KEY = 'croProjectTracker.expanded';
export const readExpanded = (storage = typeof window !== 'undefined' ? window.localStorage : null) => {
  try {
    return storage.getItem(EXPANDED_STORAGE_KEY) !== '0';
  } catch (e) {
    return true; // storage blocked or missing: the default
  }
};

// ---- periods: the sheet's Week 1-4 (P94:T97) plus the whole month ----
export const PERIODS = [
  { key: 'month', label: 'Full month' },
  { key: 'w1', label: 'Week 1', fromDay: 1, toDay: 7 },
  { key: 'w2', label: 'Week 2', fromDay: 8, toDay: 14 },
  { key: 'w3', label: 'Week 3', fromDay: 15, toDay: 21 },
  { key: 'w4', label: 'Week 4', fromDay: 22, toDay: null }, // to month end, like the sheet's 22-31
];

export const periodRange = (monthStart, periodKey) => {
  const start = dayjs(monthStart).startOf('month');
  const p = PERIODS.find((x) => x.key === periodKey);
  if (!p || p.key === 'month') {
    return { isWeek: false, fromDate: start.format('YYYY-MM-DD'), toDate: start.endOf('month').format('YYYY-MM-DD') };
  }
  const to = p.toDay ? start.date(p.toDay) : start.endOf('month');
  return { isWeek: true, fromDate: start.date(p.fromDay).format('YYYY-MM-DD'), toDate: to.format('YYYY-MM-DD') };
};

// ---- grid columns: one definition drives the table, the Copy button and the Excel export ----
// `target: true` columns are monthly figures - hidden in a week view where TAR would be misleading.
export const NUMERIC_COLUMNS = [
  {
    key: 'noOfSales',
    label: 'No of Sales',
    short: '# Sales', // used when the column is too narrow for the full label
    kind: 'count',
    drill: 'sales',
    color: '#334155',
    tip: 'Students whose first real payment fell in this period (hold amounts excluded). One student counts once.',
  },
  {
    key: 'sales',
    label: 'Sales (₹)',
    short: 'Sales', // used when the column is too narrow for the full label
    kind: 'money',
    drill: 'sales',
    color: '#334155',
    tip: 'Course value sold in this period: tuition as it was when sold + admission fee.',
  },
  {
    key: 'salesRevenue',
    label: 'Sales Revenue (₹)',
    short: 'Sales Rev', // used when the column is too narrow for the full label
    kind: 'money',
    drill: 'salesRevenue',
    color: '#047857',
    tip: 'Money collected in this period on sales made in this period. A sale is dated by its first payment (holds do not start a sale); hold amounts always count here.',
  },
  {
    key: 'feeRevenue',
    label: 'Fee Revenue (₹)',
    short: 'Fee Rev', // used when the column is too narrow for the full label
    kind: 'money',
    drill: 'feeRevenue',
    color: '#B45309',
    tip: 'Money collected in this period on sales first paid in an earlier period (instalments). E.g. first payment last month, next payment this month = this month\'s Fee Revenue.',
  },
  {
    key: 'totalRevenue',
    label: 'Total Revenue (₹)',
    short: 'Total Rev', // used when the column is too narrow for the full label
    kind: 'money',
    drill: 'totalRevenue',
    color: '#0D47A1',
    bold: true,
    tip: 'Sales Revenue + Fee Revenue. Matches the Department Income Tracker.',
  },
  {
    key: 'target',
    label: 'Total Target (₹)',
    short: 'Target', // used when the column is too narrow for the full label
    kind: 'money',
    drill: 'target',
    color: '#334155',
    target: true,
    tip: 'Monthly target from Set CRO Targets. A single total target - there is no separate sales/fee target.',
  },
  {
    key: 'tar',
    label: 'TAR',
    short: 'TAR', // used when the column is too narrow for the full label
    kind: 'tar',
    drill: 'tar',
    color: '#334155',
    target: true,
    tip: 'Total Revenue ÷ Total Target. Colours: green 100%+, amber 75–99%, red below 75%.',
  },
];

export const sumRows = (rows) => {
  const t = { noOfSales: 0, sales: 0, salesRevenue: 0, feeRevenue: 0, totalRevenue: 0, target: 0 };
  rows.forEach((r) => {
    Object.keys(t).forEach((k) => {
      t[k] += Number(r[k]) || 0;
    });
  });
  t.tar = t.target > 0 ? (t.totalRevenue / t.target) * 100 : null;
  return t;
};

export const croCount = (rows) => rows.filter((r) => !r.isCrm).length;

// a CRO "has activity" if anything is non-zero - used by the "Hide no-activity" toggle
export const hasActivity = (r) => r.totalRevenue > 0 || r.noOfSales > 0 || r.sales > 0 || r.target > 0;

// `compact` = lakh / crore for amounts, `tarDecimals` = how many decimals the TAR shows (a narrow column drops the decimal)
export const cellText = (col, row, { compact = false, tarDecimals = 1 } = {}) => {
  if (col.kind === 'tar') return pct(row.tar, tarDecimals);
  if (col.kind === 'count') return dashZero(row[col.key]);
  return compact ? compactDash(row[col.key]) : dashZero(row[col.key]);
};

// Sorting lives with the page so the grid, Copy and Export all use the same on-screen order.
const sortValue = (row, key) => {
  if (key === 'name') return row.name.toLowerCase();
  if (key === 'project') return row.project || '';
  if (key === 'team') return (row.teamName || 'No CRM').toLowerCase();
  return row[key];
};
export const nextSort = (current, key) =>
  current.key === key
    ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'name' || key === 'project' || key === 'team' ? 'asc' : 'desc' };
// The page opens sorted BY PROJECT: every team of the first project together, then the next project's, and so on, in the order the projects
// are listed (Foundation, Classroom, ...). `projectOrder` = { projectKey: position }. Inside a project the biggest earners come first.
// Sorting by project the other way round (dir 'desc') reverses the projects, not the order inside them.
export const projectOrderOf = (projects = []) => Object.fromEntries(projects.map((p, i) => [p.key, i]));
const projectRank = (order, key) => (order && Object.prototype.hasOwnProperty.call(order, key) ? order[key] : null);
const compareProjects = (a, b, order) => {
  const ra = projectRank(order, a.projectKey);
  const rb = projectRank(order, b.projectKey);
  if (ra !== null && rb !== null) return ra - rb;
  return String(a.project || '').localeCompare(String(b.project || '')); // no order known: by the project's name
};

export const sortRows = (rows, { key, dir }, projectOrder = {}) => {
  const m = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === 'project') {
      const byProject = compareProjects(a, b, projectOrder) * m;
      return byProject || b.totalRevenue - a.totalRevenue || a.name.localeCompare(b.name);
    }
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    // empty TARs always sink to the bottom, whichever way we sort
    if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
    if (bv === null || bv === undefined) return -1;
    if (typeof av === 'string') return av.localeCompare(bv) * m;
    return (av - bv) * m || a.name.localeCompare(b.name);
  });
};

// Group the (already filtered + sorted) CRO rows into teams: one group per CRM (the CRO's reporting
// head) and project. Teams are ordered by the current sort (a name sort = team name, otherwise the
// team's subtotal for that column; a project sort = project by project, see sortRows); "No CRM" goes last (last within its project for a
// project sort). Rows inside a team keep their order.
export const groupRows = (rows, sort, projectOrder = {}) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = `${r.teamId || 0}|${r.projectKey}`;
    if (!map.has(key)) {
      map.set(key, { key, teamId: r.teamId || 0, teamName: r.teamName || 'No CRM', headActive: r.teamHeadActive !== false, projectKey: r.projectKey, project: r.project, rows: [] });
    }
    map.get(key).rows.push(r);
  });
  const m = sort.dir === 'asc' ? 1 : -1;
  // the CRM's own line (their personal income / sales) is pinned to the top of their team
  const groups = [...map.values()].map((g) => ({
    ...g,
    rows: [...g.rows.filter((r) => r.isCrm), ...g.rows.filter((r) => !r.isCrm)],
    total: sumRows(g.rows),
  }));
  groups.sort((a, b) => {
    if (sort.key === 'project') {
      // all of a project's teams together (projects in their listed order); inside a project the bigger teams first and "No CRM" last
      const byProject = compareProjects(a, b, projectOrder) * m;
      if (byProject) return byProject;
      if (a.teamId === 0 && b.teamId !== 0) return 1;
      if (b.teamId === 0 && a.teamId !== 0) return -1;
      return b.total.totalRevenue - a.total.totalRevenue || a.teamName.localeCompare(b.teamName);
    }
    if (a.teamId === 0 && b.teamId !== 0) return 1;
    if (b.teamId === 0 && a.teamId !== 0) return -1;
    if (sort.key === 'name') return a.teamName.localeCompare(b.teamName) * m;
    const av = a.total[sort.key];
    const bv = b.total[sort.key];
    if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
    if (bv === null || bv === undefined) return -1;
    return (av - bv) * m || a.teamName.localeCompare(b.teamName);
  });
  return groups;
};

// ---- drill-down: what a click on a number asks for ----
// metric = which list (sales | salesRevenue | feeRevenue | totalRevenue | target) or 'tar' (a formula,
// no list). scope narrows the list to the clicked cell: { userIds, projectKey, ogd, groupBy }.
const cleanLabel = (label) => label.replace(' (₹)', '');
export const buildDrill = (col, row, scope, who) =>
  col.drill === 'tar'
    ? { metric: 'tar', title: `${who} — TAR`, formula: { kind: 'tar', revenue: row.totalRevenue, target: row.target, tar: row.tar } }
    : { metric: col.drill, title: `${who} — ${cleanLabel(col.label)}`, scope };
